import { Box, type ChakraProps } from '@chakra-ui/react';
import { Country, type Country as countryCode, countriesInfo } from '@/models/country';
import { countryFlagIconCode } from './countryFlagCodes';
import { useTbdFlagStyle } from './useTbdFlagStyle';

/** `flag-icons` 4×3 ratio (width = 4/3 × height). */
export const FLAG_ASPECT_RATIO = 4 / 3;

type FlagProps = {
  country: countryCode;
  hasShadow?: boolean;
  /** @deprecated Ignored; all flags use flag-icons 4×3 SVGs. */
  svg?: boolean;
} & ChakraProps;

export function Flag(props: FlagProps) {
  const { country, hasShadow, svg: _ignoredSvg, w, width, h, height, opacity, ...rest } = props;
  const tbdStyle = useTbdFlagStyle();
  const code = countryFlagIconCode[country];
  const label = countriesInfo[country]?.name ?? country;

  const resolvedW = w ?? width;
  const resolvedH = h ?? height;

  let boxW: ChakraProps['w'];
  let boxH: ChakraProps['h'];

  const bothDimensionsSet = resolvedW !== undefined && resolvedH !== undefined;

  if (bothDimensionsSet) {
    boxW = resolvedW;
    boxH = resolvedH;
  } else if (resolvedW !== undefined) {
    boxW = resolvedW;
    boxH = undefined;
  } else if (resolvedH !== undefined) {
    boxW = undefined;
    boxH = resolvedH;
  } else {
    boxW = 6;
    boxH = undefined;
  }

  /** Height-only callers (edit list, rank preview) need aspect ratio so width is not zero. */
  const useAspectRatio = !bothDimensionsSet;

  if (country === Country.TBD) {
    return (
      <Box
        as='span'
        display='inline-block'
        lineHeight={0}
        verticalAlign='middle'
        overflow='hidden'
        borderRadius='3px'
        aspectRatio={useAspectRatio ? FLAG_ASPECT_RATIO : undefined}
        w={boxW}
        h={boxH}
        flexShrink={0}
        boxShadow={hasShadow ? '0px 0px 3px 0.3px #3336' : undefined}
        borderWidth='1px'
        borderStyle='solid'
        borderColor={tbdStyle.borderColor}
        bg={tbdStyle.bg}
        opacity={opacity ?? tbdStyle.opacity}
        title={label}
        role='img'
        aria-label={`${label} flag`}
        {...rest}
      />
    );
  }

  return (
    <Box
      as='span'
      display='inline-block'
      lineHeight={0}
      verticalAlign='middle'
      overflow='hidden'
      borderRadius='3px'
      aspectRatio={useAspectRatio ? FLAG_ASPECT_RATIO : undefined}
      w={boxW}
      h={boxH}
      flexShrink={0}
      boxShadow={hasShadow ? '0px 0px 3px 0.3px #3336' : undefined}
      position='relative'
      {...rest}
    >
      <Box
        as='span'
        className={`fi fi-${code}`}
        position='absolute'
        inset={0}
        display='block'
        width='100%'
        height='100%'
        backgroundSize='cover'
        backgroundPosition='center'
        backgroundRepeat='no-repeat'
        title={label}
        role='img'
        aria-label={`${label} flag`}
        sx={{
          fontSize: 0,
          lineHeight: 0,
          '&::before': {
            content: 'none',
            padding: 0,
          },
        }}
      />
    </Box>
  );
}
